import {
  type CompiledProject,
  type VideoProject,
  buildProject,
  validateProject,
} from '@cinematic-engine/video-dsl';
import { type SceneBlock, parseScript } from './script-parser';
import { type ShotPlan, planScenes } from './shot-planner';

export interface DirectorOptions {
  projectId?: string;
  title?: string;
  format?: { preset: string; width: number; height: number; fps: number };
  language?: string;
}

export interface DirectedOutput {
  project: VideoProject;
  compiled: CompiledProject;
  sceneCount: number;
  shotCount: number;
  durationSeconds: number;
}

const DEFAULT_FORMAT = { preset: 'cinema-16-9', width: 1920, height: 1080, fps: 24 };

export function generateProjectFromScript(
  script: string,
  options: DirectorOptions = {}
): DirectedOutput {
  const scenes = parseScript(script);
  if (scenes.length === 0) {
    throw new Error('Roteiro vazio: escreva pelo menos uma cena.');
  }
  return buildDirectedProject(scenes, planScenes(scenes), options);
}

export function buildDirectedProject(
  scenes: SceneBlock[],
  shots: ShotPlan[],
  options: DirectorOptions = {}
): DirectedOutput {
  const format = options.format ?? DEFAULT_FORMAT;
  const language = options.language ?? 'pt-BR';

  const project: VideoProject = {
    version: '1.0',
    schemaVersion: 1,
    id: options.projectId ?? `directed-${Date.now().toString(36)}`,
    metadata: {
      title: options.title ?? cleanSceneTitle(scenes[0]?.title) ?? 'Projeto Dirigido',
      language,
      description: 'Gerado pelo AI Director a partir de um roteiro',
    },
    format,
    style: { preset: 'cinematic' },
    assets: shots.map((shot) => ({
      id: `asset-${shot.shotId}`,
      type: 'image',
      name: shot.title,
      source: { kind: 'generated' },
      metadata: { width: format.width, height: format.height },
    })),
    scenes: shots.map((shot, index) => ({
      id: shot.sceneId,
      title: shot.title,
      duration: sceneDuration(scenes[index], shot),
      shots: [
        {
          id: shot.shotId,
          start: 0,
          duration: sceneDuration(scenes[index], shot),
          purpose: shot.purpose,
          emotion: shot.emotion,
          energy: shot.energy,
          camera: {
            type: 'perspective',
            position: { x: 0, y: 0, z: 5 },
            rotation: { x: 0, y: 0, z: 0 },
            target: { x: 0, y: 0, z: 0 },
            fov: 35,
            near: 0.1,
            far: 1000,
            movement: shot.cameraMovement,
          },
          subjects: [{ assetId: `asset-${shot.shotId}`, usage: 'background' }],
          environment: [],
          effects: shot.effects,
          typography: shot.typography,
          audio: [],
          transition: { type: 'fade', duration: 0.5 },
        },
      ],
    })),
    audio: {
      tracks: [
        { id: 'music-track', name: 'Music', kind: 'music', clips: [] },
        { id: 'voice-track', name: 'Voice', kind: 'voice', clips: [] },
      ],
    },
    responsive: [],
    output: {},
  };

  const validation = validateProject(project);
  if (!validation.valid) {
    const detail = validation.issues
      .filter((issue) => issue.severity === 'error')
      .map((issue) => `${issue.code} ${issue.path}: ${issue.message}`)
      .join('\n');
    throw new Error(`Direção inválida:\n${detail}`);
  }

  return {
    project,
    compiled: buildProject(project),
    sceneCount: scenes.length,
    shotCount: shots.length,
    durationSeconds: project.scenes.reduce((acc, scene) => acc + (scene.duration ?? 0), 0),
  };
}

function sceneDuration(scene: { estimatedSeconds: number }, _shot: { shotId: string }): number {
  return Math.max(2, scene.estimatedSeconds);
}

function cleanSceneTitle(title: string | undefined): string | null {
  if (!title) return null;
  return (
    title
      .replace(/^(cena|scene)\s*\d*\s*[:.-]*\s*/i, '')
      .replace(/^(int\.|ext\.|int\.\/ext\.|int\/ext)[\s.:-]*/i, '')
      .trim() || null
  );
}
